import React from 'react';
import { TrendingUp, Timer, Volume2 } from 'lucide-react';

import { Trans, useLingui } from '@lingui/react/macro';

type TFunction = ReturnType<typeof useLingui>['t'];

interface BellCurveProps {
  value: number;
  mean: number;
  stdDev: number;
  label: string;
  unit: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}

// Normal distribution data for voice characteristics
// Note: descriptions are not translated to avoid i18n issues during SSR
export const VOICE_NORMAL_RANGES = {
  pitch: {
    mean: 165, // Hz - average speaking pitch
    stdDev: 25, // Standard deviation
    min: 80,
    max: 300,
    description: 'Average speaking pitch for most people'
  },
  speakingRate: {
    mean: 150, // WPM - words per minute
    stdDev: 30,
    min: 100,
    max: 250,
    description: 'Typical speaking rate for presentations'
  },
  volume: {
    mean: 65, // dB - approximate volume level
    stdDev: 10,
    min: 40,
    max: 90,
    description: 'Normal conversational volume'
  }
} as const;

// Calculate percentile position on bell curve
function calculatePercentile(value: number, mean: number, stdDev: number): number {
  // Z-score calculation
  const zScore = (value - mean) / stdDev;
  
  // Approximate percentile using normal distribution
  // This is a simplified approximation
  if (zScore <= -2) return 2.5;
  if (zScore <= -1.5) return 6.7;
  if (zScore <= -1) return 15.9;
  if (zScore <= -0.5) return 30.9;
  if (zScore <= 0) return 50;
  if (zScore <= 0.5) return 69.1;
  if (zScore <= 1) return 84.1;
  if (zScore <= 1.5) return 93.3;
  if (zScore <= 2) return 97.5;
  return 99;
}

// Generate bell curve points for SVG
function generateBellCurvePoints(mean: number, stdDev: number, width: number, height: number, sidePadding: number = 20) {
  const points = [];
  const xMin = mean - 3 * stdDev;
  const xMax = mean + 3 * stdDev;
  const xRange = xMax - xMin;
  const usableWidth = width - (sidePadding * 2);
  
  for (let i = 0; i <= usableWidth; i += 2) {
    const x = xMin + (i / usableWidth) * xRange;
    const y = Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
    const yPos = height - (y * height * 0.8) - 10; // Scale and offset
    points.push(`${sidePadding + i},${yPos}`);
  }
  
  return points.join(' ');
}

// Get interpretation text based on percentile
function getInterpretation(t: TFunction, percentile: number, label: string): string {
  if (percentile < 10) {
    return t`Your ${label.toLowerCase()} is in the lower range (${percentile.toFixed(0)}th percentile)`;
  } else if (percentile < 25) {
    return t`Your ${label.toLowerCase()} is below average (${percentile.toFixed(0)}th percentile)`;
  } else if (percentile < 75) {
    return t`Your ${label.toLowerCase()} is within the normal range (${percentile.toFixed(0)}th percentile)`;
  } else if (percentile < 90) {
    return t`Your ${label.toLowerCase()} is above average (${percentile.toFixed(0)}th percentile)`;
  } else {
    return t`Your ${label.toLowerCase()} is in the higher range (${percentile.toFixed(0)}th percentile)`;
  }
}

// Get detailed explanation based on percentile and characteristic type
function getDetailedExplanation(t: TFunction, percentile: number, type: string): string {
  switch (type) {
    case 'pitch':
      if (percentile < 25) {
        return t`Lower pitch can sound more authoritative and calm, but may need more variation for engagement.`;
      } else if (percentile > 75) {
        return t`Higher pitch can sound more energetic and friendly, but may need to be balanced with lower tones.`;
      } else {
        return t`Your pitch is in the typical range - great for clear communication and audience engagement.`;
      }
    case 'speakingRate':
      if (percentile < 25) {
        return t`Speaking slower can improve comprehension but may lose audience attention - try adding more energy.`;
      } else if (percentile > 75) {
        return t`Speaking faster shows energy but may be hard to follow - try adding strategic pauses.`;
      } else {
        return t`Your speaking rate is ideal for most audiences - clear and engaging without being rushed.`;
      }
    case 'volume':
      if (percentile < 25) {
        return t`Lower volume may sound uncertain - try projecting more confidence in your voice.`;
      } else if (percentile > 75) {
        return t`Higher volume shows confidence but may be overwhelming - balance with softer moments.`;
      } else {
        return t`Your volume is well-balanced - confident without being overpowering.`;
      }
    default:
      return t`Your voice characteristics are well-suited for effective communication.`;
  }
}

// Get color based on percentile
function getPercentileColor(percentile: number): string {
  if (percentile < 10 || percentile > 90) {
    return 'text-brand-error'; // Red for extremes
  } else if (percentile < 25 || percentile > 75) {
    return 'text-brand-gold'; // Gold for outside normal
  } else {
    return 'text-brand-green'; // Green for normal range
  }
}

export const BellCurve: React.FC<BellCurveProps> = ({
  value,
  mean,
  stdDev,
  label,
  unit,
  icon,
  color,
  description
}) => {
  const { t } = useLingui();
  const percentile = calculatePercentile(value, mean, stdDev);
  const interpretation = getInterpretation(t, percentile, label);
  const percentileColor = getPercentileColor(percentile);
  const detailedExplanation = getDetailedExplanation(t, percentile, label.toLowerCase().replace(' ', ''));
  
  // Calculate position on curve (0-100%)
  const xMin = mean - 3 * stdDev;
  const xMax = mean + 3 * stdDev;
  const xRange = xMax - xMin;
  const position = Math.max(0, Math.min(100, ((value - xMin) / xRange) * 100));
  
  const width = 400;
  const height = 196;
  const sidePadding = 20; // Add padding to prevent clipping at edges
  const usableWidth = width - (sidePadding * 2);
  const curvePoints = generateBellCurvePoints(mean, stdDev, width, height, sidePadding);
  
  return (
    <div className="p-3 sm:p-6 rounded-lg border-2 border-brand-dark-blue/20 bg-gradient-to-br from-brand-dark-blue/5 to-transparent">
      {/* <div className="flex items-center gap-2 mb-3">
        <div className={`p-1 rounded ${color}`}>
          {icon}
        </div>
        <h4 className="text-lg font-bold text-brand-dark-blue">{label}</h4>
      </div> */}
      
      {/* Bell Curve Visualization */}
      <div className="relative mb-4 sm:mb-6 pb-2 sm:pb-4 overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height + 5}`} className="w-full max-w-lg mx-auto" preserveAspectRatio="xMidYMid meet">
          {/* Bell curve */}
          <polyline
            points={curvePoints}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-brand-dark-blue/60"
          />
          
          {/* Fill under curve */}
          <polygon
            points={`${sidePadding},${height - 10} ${curvePoints} ${width - sidePadding},${height - 10}`}
            fill="currentColor"
            fillOpacity="0.1"
            className="text-brand-dark-blue/20"
          />
          
          {/* Mean line */}
          <line
            x1={width * 0.5}
            y1={10}
            x2={width * 0.5}
            y2={height - 10}
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="3,3"
            className="text-brand-gold/60"
          />
          
          {/* User position indicator */}
          <circle
            cx={sidePadding + (position * usableWidth / 100)}
            cy={height - 20}
            r="6"
            fill="currentColor"
            className="text-brand-gold"
          />
          <circle
            cx={sidePadding + (position * usableWidth / 100)}
            cy={height - 20}
            r="3"
            fill="white"
          />
          
          {/* Range indicators */}
          <text
            x={sidePadding}
            y={height}
            className="text-[10px] sm:text-sm fill-current text-brand-dark-blue/50"
          >
            {t`Low`}
          </text>
          <text
            x={width * 0.5}
            y={height}
            textAnchor="middle"
            className="text-[10px] sm:text-sm fill-current text-brand-dark-blue/70"
          >
            {t`Average`}
          </text>
          <text
            x={width - sidePadding}
            y={height}
            textAnchor="end"
            className="text-[10px] sm:text-sm fill-current text-brand-dark-blue/50"
          >
            {t`High`}
          </text>

          {/* User position label */}
          <text
            x={sidePadding + (position * usableWidth / 100)}
            y={height - 25}
            textAnchor="middle"
            className="text-[11px] sm:text-sm fill-current text-brand-gold font-bold"
          >
            {t`You`}
          </text>
        </svg>
      </div>
      
      {/* Value and Interpretation */}
      <div className="space-y-2">
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-sm sm:text-base text-brand-dark-blue/70"><Trans>Your {label}:</Trans></span>
          <span className="text-base sm:text-lg font-bold text-brand-dark-blue">
            {value.toFixed(0)} {unit}
          </span>
        </div>
        
        {/* <div className="flex justify-between items-center">
          <span className="text-sm text-brand-dark-blue/70">Percentile:</span>
          <span className={`text-sm font-bold ${percentileColor}`}>
            {percentile.toFixed(0)}th
          </span>
        </div> */}
        
        {/* <p className="text-xs text-brand-dark-blue/60 mt-2">
          {interpretation}
        </p> */}
        
        <p className="text-xs sm:text-sm text-brand-dark-blue/70 mt-2 font-medium leading-relaxed">
          {detailedExplanation}
        </p>
        
        {/* <p className="text-xs text-brand-dark-blue/50 mt-1">
          {description}
        </p> */}
      </div>
    </div>
  );
};

// Helper component for voice characteristics
export const VoiceCharacteristicBellCurve: React.FC<{
  type: 'pitch' | 'speakingRate' | 'volume';
  value: number;
  showTips?: boolean;
}> = ({ type, value, showTips = false }) => {
  const { t } = useLingui();
  const config = VOICE_NORMAL_RANGES[type];
  const percentile = calculatePercentile(value, config.mean, config.stdDev);

  let icon, color, label, unit, description, tips;

  switch (type) {
    case 'pitch':
      icon = <TrendingUp className="h-5 w-5 text-brand-gold" />;
      color = "bg-brand-gold/20";
      label = t`Pitch`;
      unit = t`Hz`;
      description = t`Pitch refers to how high or low your voice sounds. It's measured in Hertz (Hz) and affects how authoritative, friendly, or engaging you sound.`;
      tips = [
        t`Vary your pitch throughout your speech to maintain audience interest`,
        t`Use higher pitch for questions and excitement, lower pitch for authority`,
        t`Avoid monotone delivery - pitch variation is key to engaging presentations`,
        t`Practice pitch exercises like humming scales to improve vocal range`
      ];
      break;
    case 'speakingRate':
      icon = <Timer className="h-5 w-5 text-brand-dark-blue" />;
      color = "bg-brand-dark-blue/20";
      label = t`Speaking Rate`;
      unit = t`WPM`;
      description = t`Speaking rate is how fast you deliver your words, measured in words per minute (WPM). The ideal rate keeps your audience engaged without overwhelming them.`;
      tips = [
        t`Aim for 150-160 WPM for most presentations and conversations`,
        t`Slow down for complex topics or important points`,
        t`Speed up slightly for exciting or energetic content`,
        t`Use pauses strategically to let important points sink in`,
        t`Practice with a metronome to develop consistent pacing`
      ];
      break;
    case 'volume':
      icon = <Volume2 className="h-5 w-5 text-brand-green" />;
      color = "bg-brand-green/20";
      label = t`Volume`;
      unit = t`dB`;
      description = t`Volume is how loud or soft your voice is, measured in decibels (dB). Proper volume ensures your message is heard clearly without being overwhelming.`;
      tips = [
        t`Project your voice from your diaphragm, not your throat`,
        t`Adjust volume based on room size and audience distance`,
        t`Use volume changes to emphasize key points`,
        t`Avoid speaking too softly - it can sound uncertain`,
        t`Practice breathing exercises to support stronger voice projection`
      ];
      break;
  }
  
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Bell Curve Visualization */}
      <BellCurve
        value={value}
        mean={config.mean}
        stdDev={config.stdDev}
        label={label}
        unit={unit}
        icon={icon}
        color={color}
        description={config.description}
      />
      
      {/* Description Section */}
      <div className="p-4 sm:p-6 rounded-lg border border-brand-dark-blue/10 bg-gradient-to-r from-brand-dark-blue/5 to-transparent">
        <h4 className="text-base sm:text-lg font-semibold text-brand-dark-blue mb-2 sm:mb-3 flex items-center gap-2">
          {icon}
          <Trans>About {label}</Trans>
        </h4>
        <p className="text-xs sm:text-sm text-brand-dark-blue/80 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Tips Section */}
      {showTips && (
        <div className="p-4 sm:p-6 rounded-lg border-2 border-brand-gold/20 bg-gradient-to-br from-brand-gold/5 to-transparent">
          <h4 className="text-base sm:text-lg font-bold mb-3 text-brand-gold flex items-center gap-2">
            <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
            <Trans>{label} Improvement Tips</Trans>
          </h4>
          <ul className="space-y-2 sm:space-y-3">
            {tips.map((tip: string, index: number) => (
              <li key={index} className="flex items-start gap-2 sm:gap-3">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-gold rounded-full shrink-0 mt-1.5 sm:mt-2" />
                <span className="text-xs sm:text-sm text-brand-dark-blue leading-relaxed">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
